/**
 * AuditLog — the admin accountability view. Every logged action (who did what,
 * to which entity, when, from where) in a filterable, paginated table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, Paper, TextField, MenuItem, Typography,
  CircularProgress, useTheme, alpha, Tooltip,
} from '@mui/material';
import { History } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { PageHeader, EmptyState, panelSx } from '../ui';

const ACTION_TINT = (action) => {
  if (/deleted|deactivated|refunded|cancelled|rejected/.test(action)) return 'error';
  if (/created|approved|published|confirmed|sent/.test(action)) return 'success';
  if (/updated|shared|responded|action|export|bulk/.test(action)) return 'warning';
  return 'default';
};

const AuditLog = () => {
  const theme = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [action, setAction] = useState('');
  const [actions, setActions] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminService.getActivityLogs({ page: page + 1, per_page: perPage, action_type: action || undefined });
      const list = res?.logs ?? res?.activity_logs ?? res?.data?.logs ?? (Array.isArray(res) ? res : []);
      setLogs(list);
      setTotal(res?.pagination?.total_count ?? res?.pagination?.total ?? list.length);
      // Build the action filter options from what we see.
      setActions((prev) => Array.from(new Set([...prev, ...list.map((l) => l.action)])).sort());
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, action]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <PageHeader title="Audit Log" subtitle="A complete, immutable record of admin activity." />

      <Paper elevation={0} sx={{ ...panelSx(theme), mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select label="Action" size="small" value={action}
          onChange={(e) => { setAction(e.target.value); setPage(0); }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All actions</MenuItem>
          {actions.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">{total} entries</Typography>
      </Paper>

      <TableContainer component={Paper} elevation={0} sx={{ ...panelSx(theme), p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : logs.length === 0 ? (
          <EmptyState icon={<History sx={{ fontSize: 56 }} />} title="No activity yet" description="Admin actions will appear here as they happen." minHeight="30vh" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                <TableCell>When</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={new Date(log.created_at).toLocaleString()}>
                      <span>{new Date(log.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{log.user?.email || log.user?.full_name || log.user_email || 'system'}</TableCell>
                  <TableCell><Chip size="small" label={log.action} color={ACTION_TINT(log.action)} sx={{ fontWeight: 600 }} /></TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{log.entity_type ? `${log.entity_type}#${String(log.entity_id).slice(0, 8)}` : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                      {log.details ? JSON.stringify(log.details) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>{log.ip_address || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default AuditLog;
